/* VIP Unique Number Cart Guard (#006)
 * Every VIP number (single or family bundle) is unique: max 1 per cart line.
 * - Blocks adding a unique number that is already in the cart (all add paths, incl. Buy Now).
 * - Serialises cart adds so rapid clicks cannot race.
 * - After any cart change, on page load and before checkout, caps unique lines at quantity 1
 *   (only lowers quantity on lines that exceed 1; never removes items).
 * Unique = cart line SKU made only of 11-digit 07 numbers (e.g. 07700900123 or 07700900123-07700900124).
 * Other products (eSIM, custom requests) are not touched.
 *
 * v138 Reserve rule (2026-10-25): a Pre-Order reservation is always checked out on its own.
 * - Before any normal add (Available Number, eSIM; PDP, Quick Add, search, home page, cart drawer),
 *   an abandoned Pre-Order reservation still in the basket is removed first, so mixed baskets cannot form.
 * - On page load, a basket holding a Pre-Order line together with other items has the Pre-Order line removed
 *   (the other items are never touched).
 * - Adds that carry the _reservation property (the Reserve button) are left alone.
 * Pre-Order line = _reservation property, or product tagged "preorder" (looked up from /products/<handle>.js).
 *
 * v138 Reserve (owner decision 2026-10-25 18:25, replaces the 18:18 "clear everything" rule):
 * window.VIPReserve.go():
 *   - empty basket: add only the selected Pre-Order number (quantity 1, _reservation property) and go straight to checkout;
 *   - basket holds only abandoned reservations: remove them, add the selected number, go straight to checkout;
 *   - basket holds Available Numbers / eSIMs: nothing is changed; the caller shows VIPReserve.BASKET_MSG.
 * Before redirecting it confirms the basket holds exactly that one line. No cart page, popup or confirmation step.
 * Used by the product page Reserve button and by every Pre-Order listing "Reserve" button (.por-btn: collection,
 * pages, filtered results, home page section), found by event delegation so AJAX-loaded rows work too.
 * If a number cannot be reserved online (sold or not eligible), the listing button falls back to its WhatsApp link.
 *
 * v160 (2026-11-01) speed changes – every rule above is unchanged:
 * - Cart reads go through assets/vip-cart-core.js, so the checks before and after an add share one read and the
 *   cart returned by the add itself (no extra /cart.js requests).
 * - Continue to checkout: the cart is checked in the background as soon as it changes (reservations, mixed basket,
 *   quantities, pre-order tags). On tap one fresh /cart.js read (started on pointer-down) confirms the cart is
 *   exactly the one that was checked; if so the browser leaves at once, otherwise the full check runs as before.
 * - Reserve: one fresh basket read; an empty basket is not cleared (nothing to clear); the verified cart comes back
 *   with the add; if the basket turns out to hold anything other than reservations, nothing is cleared – only the
 *   line just added is taken out again and the basket message is shown.
 * - Reserve listing buttons carry the variant id and pre-order flag (data-vid / data-po), saving a lookup; Shopify
 *   still refuses the add if the number has just been sold.
 */
(function () {
  "use strict";
  if (window.__vipUniqueGuard) return;
  window.__vipUniqueGuard = true;

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch || !window.Promise) return;

  var UNIQUE_SKU = /^07\d{9}(-07\d{9})*$/;
  var DUP_MSG = "This number is already in your cart";
  var chain = Promise.resolve();          // serialises add requests
  var normalising = null;                 // shared in-flight normalise promise

  function isUnique(line) {
    var sku = String((line && line.sku) || "").replace(/\s+/g, "");
    return UNIQUE_SKU.test(sku);
  }
  function pathOf(url) {
    try { return new URL(url, location.href).pathname.replace(/\/+$/, ""); } catch (e) { return ""; }
  }
  // Matches /cart/add, /cart/add.js (also with a locale prefix such as /en-gb/cart/add.js)
  function kind(url) {
    var p = pathOf(url);
    if (/\/cart\/add(\.js)?$/.test(p)) return "add";
    if (/\/cart\/(change|update)(\.js)?$/.test(p)) return "mutate";
    return null;
  }
  function getCart() {
    return nativeFetch("/cart.js", { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); });
  }
  function setLine(key, qty) {
    return nativeFetch("/cart/change.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }

  // ---- Pre-Order reservation helpers ----
  var tagCache = {};
  function isPreorderLine(it) {
    if (it && it.properties && it.properties._reservation === "1") return Promise.resolve(true);
    if (!it || !it.handle || !isUnique(it)) return Promise.resolve(false);   // eSIMs and other products are never pre-orders
    if (tagCache.hasOwnProperty(it.handle)) return Promise.resolve(tagCache[it.handle]);
    return nativeFetch("/products/" + it.handle + ".js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { var v = !!(p && p.tags && p.tags.indexOf("preorder") !== -1); tagCache[it.handle] = v; return v; })
      .catch(function () { return false; });
  }
  // Remove Pre-Order lines. onlyIfMixed: only when the basket also holds other items. Resolves true if removed.
  function dropReservations(onlyIfMixed) {
    return getCart().then(function (cart) {
      var items = cart.items || [];
      return Promise.all(items.map(isPreorderLine)).then(function (flags) {
        var pre = items.filter(function (it, i) { return flags[i]; });
        if (!pre.length) return false;
        if (onlyIfMixed && pre.length === items.length) return false;
        return pre.reduce(function (p, it) {
          return p.then(function () { return setLine(it.key, 0); });
        }, Promise.resolve()).then(function () {
          try { if (window.VIPCart && window.VIPCart.refresh) window.VIPCart.refresh(); } catch (e) {}
          return true;
        });
      });
    }).catch(function () { return false; });
  }
  function bodyIsReservation(body) {
    try {
      if (typeof body === "string" && body.trim().charAt(0) === "{") {
        var j = JSON.parse(body);
        var list = (j.items && j.items.length) ? j.items : [j];
        return list.some(function (i) { return i && i.properties && i.properties._reservation === "1"; });
      }
    } catch (e) {}
    return false;
  }

  // Cap every unique line at 1. Resolves to true if anything was changed.
  function normalise() {
    if (normalising) return normalising;
    normalising = getCart().then(function (cart) {
      var fix = (cart.items || []).filter(function (it) { return isUnique(it) && it.quantity > 1; });
      return fix.reduce(function (p, it) {
        return p.then(function () { return setLine(it.key, 1); });
      }, Promise.resolve()).then(function () { return fix.length > 0; });
    }).catch(function () { return false; }).then(function (changed) {
      normalising = null;
      if (changed) {
        try { if (window.VIPCart && window.VIPCart.refresh) window.VIPCart.refresh(); } catch (e) {}
      }
      return changed;
    });
    return normalising;
  }

  // Read variant ids + quantities from any /cart/add body format.
  function parseAdd(body) {
    var items = [];
    try {
      if (typeof body === "string") {
        var s = body.trim();
        if (s.charAt(0) === "{") {
          var j = JSON.parse(s);
          if (j.items && j.items.length) j.items.forEach(function (i) { items.push({ id: +i.id, q: +(i.quantity || 1) }); });
          else if (j.id) items.push({ id: +j.id, q: +(j.quantity || 1) });
          return { type: "json", data: j, items: items };
        }
        var sp = new URLSearchParams(s);
        if (sp.get("id")) items.push({ id: +sp.get("id"), q: +(sp.get("quantity") || 1) });
        return { type: "form", items: items };
      }
      if (body && typeof FormData !== "undefined" && body instanceof FormData) {
        if (body.get("id")) items.push({ id: +body.get("id"), q: +(body.get("quantity") || 1) });
        return { type: "formdata", items: items };
      }
    } catch (e) {}
    return { type: "unknown", items: items };
  }

  function dupResponse() {
    return new Response(JSON.stringify({ status: 422, message: "Cart Error", description: DUP_MSG }), {
      status: 422, headers: { "Content-Type": "application/json" }
    });
  }

  // Before an add: drop unique numbers already in the cart. Returns {skipAll, body}.
  function precheck(parsed, body) {
    if (!parsed.items.length) return Promise.resolve({ skipAll: false, body: body });
    return getCart().then(function (cart) {
      var inCart = {};
      (cart.items || []).forEach(function (it) { if (isUnique(it)) inCart[it.variant_id] = true; });
      var keep = parsed.items.filter(function (i) { return !inCart[i.id]; });
      if (!keep.length) return { skipAll: true };
      if (keep.length === parsed.items.length) return { skipAll: false, body: body };
      if (parsed.type === "json" && parsed.data.items) {
        var d = JSON.parse(JSON.stringify(parsed.data));
        d.items = d.items.filter(function (i) { return !inCart[+i.id]; });
        return { skipAll: false, body: JSON.stringify(d) };
      }
      return { skipAll: false, body: body };
    }).catch(function () { return { skipAll: false, body: body }; });
  }

  // ---- fetch wrapper ----
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var k = kind(url);
    var method = ((init && init.method) || (input && input.method) || "GET").toUpperCase();
    if (!k || method !== "POST") return nativeFetch(input, init);

    if (k === "mutate") {
      return nativeFetch(input, init).then(function (res) {
        return normalise().then(function (changed) {
          if (!changed) return res;
          // Return the corrected cart so callers render accurate quantities.
          return getCart().then(function (c) {
            return new Response(JSON.stringify(c), { status: 200, headers: { "Content-Type": "application/json" } });
          }).catch(function () { return res; });
        });
      });
    }

    // add: serialise + precheck + normalise
    var body = init && init.body;
    var run = function () {
      var parsed = parseAdd(body);
      var pre = bodyIsReservation(body) ? Promise.resolve(false) : dropReservations(false);
      return pre.then(function () { return precheck(parsed, body); }).then(function (pc) {
        if (pc.skipAll) return dupResponse();
        var opts = {};
        for (var p in init) if (Object.prototype.hasOwnProperty.call(init, p)) opts[p] = init[p];
        opts.body = pc.body;
        return nativeFetch(input, opts).then(function (res) {
          return normalise().then(function () { return res; });
        });
      });
    };
    var p = chain.then(run, run);
    chain = p.then(function () {}, function () {});
    return p;
  };

  // ---- XMLHttpRequest (apps that use XHR) : normalise after cart changes ----
  try {
    var XO = XMLHttpRequest.prototype.open, XS = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) { this.__vipKind = (String(m).toUpperCase() === "POST") ? kind(u) : null; return XO.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      if (this.__vipKind) this.addEventListener("loadend", function () { normalise(); });
      return XS.apply(this, arguments);
    };
  } catch (e) {}

  // ---- Non-AJAX product forms posting to /cart/add ----
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (!f || !f.action || kind(f.action) !== "add" || f.__vipOk) return;
    var idEl = f.querySelector('[name="id"]');
    if (!idEl) return;
    e.preventDefault();
    var vid = +idEl.value;
    dropReservations(false).then(getCart).then(function (cart) {
      var dup = (cart.items || []).some(function (it) { return it.variant_id === vid && isUnique(it); });
      if (dup) {
        try { if (window.VIPCart) { window.VIPCart.open(); window.VIPCart.refresh(); } } catch (x) {}
        return;
      }
      f.__vipOk = true; f.submit();
    }).catch(function () { f.__vipOk = true; f.submit(); });
  }, true);

  // ---- Before checkout: make sure no unique line exceeds 1 ----
  function fullCheckout(href) {
    dropReservations(true).then(normalise).then(function () { location.href = href; }, function () { location.href = href; });
  }
  // v160 B1/B2: background pre-validation of the current cart
  function sig(c) { return ((c && c.items) || []).map(function (it) { return it.key + ':' + it.quantity; }).join('|') + '#' + ((c && c.item_count) || 0); }
  var preState = null, preTimer = null, preTap = null;
  function prevalidate(cart) {
    var items = (cart && cart.items) || [];
    var s = sig(cart);
    return Promise.all(items.map(isPreorderLine)).then(function (flags) {
      var pre = flags.filter(function (f) { return f; }).length;
      var mixed = pre > 0 && pre < items.length;
      var over = items.some(function (it) { return isUnique(it) && it.quantity > 1; });
      preState = { sig: s, ok: !mixed && !over, at: Date.now() };
    }).catch(function () { preState = null; });
  }
  if (window.VIPCartCore) {
    window.VIPCartCore.onCart(function (c) { clearTimeout(preTimer); preTimer = setTimeout(function () { prevalidate(c); }, 30); });
  }
  function freshRead() { return window.VIPCartCore ? window.VIPCartCore.read({ fresh: true }) : getCart(); }
  function goCheckout(href) {
    var tap = (preTap && Date.now() - preTap.at < 4000) ? preTap.p : freshRead();
    preTap = null;
    tap.then(function (c) {
      if (preState && preState.ok && sig(c) === preState.sig) { location.href = href; return; }
      // v160 rev2: the cart changed since it was checked (e.g. another tab). Re-apply the Custom Numbers check on the
      // fresh cart before anything else; an invalid custom line keeps checkout paused exactly as on the cart page.
      try {
        if (window.VIPCustom && window.VIPCustom.checkCart && !window.VIPCustom.checkCart(c).ok) {
          if (window.VIPCart && window.VIPCart.refresh) window.VIPCart.refresh();
          if (window.VIPCustom.validate) window.VIPCustom.validate().catch(function () {});
          return;
        }
      } catch (e) {}
      fullCheckout(href);
    }, function () { fullCheckout(href); });
  }
  function isCheckoutAnchor(a) { return a && /^\/checkout(\/|$|\?)/.test(pathOf(a.href) + (a.search || "")); }
  document.addEventListener("pointerdown", function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!isCheckoutAnchor(a) || a.classList.contains("vcd-wait")) return;
    preTap = { at: Date.now(), p: freshRead() };
    preTap.p.catch(function () {});
  }, true);
  document.addEventListener("click", function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (isCheckoutAnchor(a)) {
      e.preventDefault();
      // v160 rev2: a checkout link marked unavailable (an add is still waiting for Shopify) never navigates,
      // whether it is tapped, clicked from the keyboard or triggered by script.
      if (a.classList.contains("vcd-wait") || a.getAttribute("aria-disabled") === "true") return;
      goCheckout(a.href);
      return;
    }
    var b = e.target && e.target.closest && e.target.closest('button[name="checkout"],input[name="checkout"]');
    if (b && b.form) { e.preventDefault(); dropReservations(true).then(normalise).then(function () { b.form.submit(); }); }
  }, true);

  // ---- On every page load: fix any cart that already exceeds the limit ----
  function onLoad() {
    dropReservations(true).then(function (dropped) {
      return normalise().then(function (changed) { return dropped || changed; });
    }).then(function (changed) {
      if (changed && /^\/cart\/?$/.test(location.pathname)) location.reload();
    });
  }

  // ---- RESERVE: one Pre-Order number per checkout, basket cleared first ----
  var RESERVE_ENABLED = true;   // v138 test theme only. Never publish without owner approval.
  function postJSON(url, obj) {
    return nativeFetch(url, { method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(obj || {}) });
  }
  function onlyThis(cart, vid) {
    var it = (cart && cart.items) || [];
    return it.length === 1 && it[0].variant_id === vid && it[0].quantity === 1;
  }
  var reserving = false;
  var C3_LINE_REMOVE = false;   // v160 C3 experiment: remove abandoned reservation lines one by one instead of clear.js
  var BASKET_MSG = ["You have items in your basket.",
    "To reserve this number, please complete your current purchase or empty your basket first.",
    "Each reservation requires a separate checkout."];
  // Resolves {ok:true} after starting the redirect, or {ok:false, reason:'basket'|'sold'|'error'|'busy'}.
  function reserveGo(vid) {
    vid = parseInt(vid, 10);
    if (!vid) return Promise.resolve({ ok: false, reason: "error" });
    if (reserving) return Promise.resolve({ ok: false, reason: "busy" });
    reserving = true;
    // Basket check (fresh read): anything other than (abandoned) Pre-Order reservations stops the reservation; nothing is removed.
    var basketCheck = function () {
      return freshRead().then(function (cart) {
        var others = (cart.items || []).filter(function (it) { return it.variant_id !== vid; });
        return Promise.all(others.map(isPreorderLine)).then(function (flags) {
          return { ok: !flags.some(function (f) { return !f; }), cart: cart };
        });
      });
    };
    var add = function () {
      return postJSON("/cart/add.js", { items: [{ id: vid, quantity: 1, properties: { _reservation: "1" } }] });
    };
    var verify = function (r) {
      if (!r.ok) return { ok: false, reason: r.status === 422 ? "sold" : "error" };
      // vip-cart-core returns the cart that came back with the add (no extra request when available)
      return getCart().then(function (c) { return onlyThis(c, vid) ? { ok: true } : { ok: false, reason: "retry" }; });
    };
    var isMyReservation = function (it) { return it.variant_id === vid && it.properties && it.properties._reservation === "1"; };
    var attempt = function (b) {
      var items = (b.cart && b.cart.items) || [];
      if (items.length === 1 && isMyReservation(items[0]) && items[0].quantity === 1) return Promise.resolve({ ok: true });  // already reserved: go
      var prep;
      if (!items.length) prep = Promise.resolve();                       // C1: empty basket – nothing to clear
      else if (C3_LINE_REMOVE) prep = items.reduce(function (p, it) { return p.then(function () { return setLine(it.key, 0); }); }, Promise.resolve());
      else prep = postJSON("/cart/clear.js");                             // only abandoned reservations here (checked above)
      return prep.then(add).then(verify);
    };
    return basketCheck().then(function (b) {
        if (!b.ok) return { ok: false, reason: "basket" };
        return attempt(b).then(function (res) {
          if (res.reason !== "retry") return res;
          // The basket changed underneath us (e.g. another tab). Check again before any clearing.
          return basketCheck().then(function (b2) {
            if (b2.ok) return attempt(b2).then(function (r2) { return r2.reason === "retry" ? { ok: false, reason: "error" } : r2; });
            // Other items are there: never remove them. Take out only the reservation line just added.
            var mine = (b2.cart.items || []).filter(isMyReservation);
            return mine.reduce(function (p, it) { return p.then(function () { return setLine(it.key, 0); }); }, Promise.resolve())
              .then(function () { try { if (window.VIPCart && window.VIPCart.refresh) window.VIPCart.refresh(); } catch (e) {} return { ok: false, reason: "basket" }; });
          });
        });
      })
      .then(function (res) {
        if (res.ok) { location.href = "/checkout"; return res; }
        reserving = false;
        if (res.reason === "retry") res.reason = "error";
        return res;
      }, function () { reserving = false; return { ok: false, reason: "error" }; });
  }
  function productInfo(handle) {
    return nativeFetch("/products/" + handle + ".js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }
  window.VIPReserve = { enabled: RESERVE_ENABLED, go: reserveGo, BASKET_MSG: BASKET_MSG };

  // Black-and-gold notice under a listing row (wraps on small screens).
  function showRowNotice(row, lines) {
    var old = document.querySelectorAll(".vip-rsv-note");
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
    var d = document.createElement("div");
    d.className = "vip-rsv-note"; d.setAttribute("role", "status"); d.setAttribute("aria-live", "polite");
    d.style.cssText = "box-sizing:border-box;width:100%;max-width:100%;margin:4px 0 10px;padding:12px 14px;background:#0f0e0a;" +
      "border:1px solid #3a3320;border-left:3px solid #c9a84c;color:#d4b85e;font-family:'Jost',sans-serif;font-size:13px;" +
      "line-height:1.6;letter-spacing:0.02em;text-align:left;white-space:normal;overflow-wrap:anywhere;";
    lines.forEach(function (t, k) {
      var p = document.createElement("div"); p.textContent = t;
      if (k === 0) { p.style.fontWeight = "600"; p.style.color = "#e6cf7a"; }
      d.appendChild(p);
    });
    row.parentNode.insertBefore(d, row.nextSibling);
  }

  // Listing rows: <div class="por-row|po-card"><a href="/products/HANDLE" ...>NUMBER</a> ... <a class="por-btn" href="https://wa.me/...">Reserve</a></div>
  document.addEventListener("click", function (e) {
    if (!RESERVE_ENABLED || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var btn = e.target && e.target.closest && e.target.closest("a.por-btn, button.por-btn");
    if (!btn) return;
    var row = btn.parentNode;
    var link = row && row.querySelector('a[href*="/products/"]');
    var m = link && (link.getAttribute("href") || "").match(/\/products\/([^/?#]+)/);
    if (!m) return;                       // unknown markup: keep the original WhatsApp link
    e.preventDefault();
    var wa = btn.getAttribute("href");
    var label = btn.textContent;
    if (btn.__vipBusy) return;
    btn.__vipBusy = true; btn.textContent = "Please wait…"; btn.setAttribute("aria-busy", "true");
    var fallback = function (text) {
      btn.textContent = text || label;
      setTimeout(function () { btn.textContent = label; btn.__vipBusy = false; btn.removeAttribute("aria-busy"); if (wa) window.open(wa, "_blank", "noopener"); }, 1200);
    };
    // v160 C4: the listing row carries the variant id and pre-order flag; otherwise look the product up as before.
    var dv = parseInt(btn.getAttribute("data-vid") || "", 10);
    var info = (dv && btn.getAttribute("data-po") === "1")
      ? Promise.resolve({ tags: ["preorder"], variants: [{ id: dv, available: true }] })
      : productInfo(m[1]);
    info.then(function (p) {
      var v = p && p.variants && p.variants[0];
      if (!p || !v || !v.available || !p.tags || p.tags.indexOf("preorder") === -1) return fallback("Ask on WhatsApp");
      return reserveGo(v.id).then(function (res) {
        if (res.ok) return;
        if (res.reason === "basket") {
          btn.textContent = label; btn.__vipBusy = false; btn.removeAttribute("aria-busy");
          showRowNotice(row, BASKET_MSG);
          return;
        }
        fallback(res.reason === "sold" ? "Just reserved – ask on WhatsApp" : "Ask on WhatsApp");
      });
    }).catch(function () { fallback(); });
  }, true);

  // v139 (2026-10-25): RESERVE in the instant number search panel (assets/vip-number-search.js, #vip-num-search-panel).
  // The panel renders each Pre-Order hit as <a class="vip-num-hit" data-group="PRE-ORDER" href="/products/HANDLE">.
  // A RESERVE button is added to those rows only (Available rows and their BUY buttons are not touched) and uses
  // exactly the same reserveGo() flow, basket rules and messages as the listing buttons above.
  var RSV_BTN_CSS = "background:#d4b85e;border:1px solid #d4b85e;color:#0f0e0a;border-radius:5px;padding:5px 8px;" +
    "font-family:Jost,sans-serif;font-size:11px;font-weight:600;letter-spacing:1px;cursor:pointer;min-width:52px;" +
    "white-space:nowrap;line-height:normal;";
  function decorateSearchPanel(panel) {
    var rows = panel.querySelectorAll('a.vip-num-hit[data-group="PRE-ORDER"]');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var cell = row.lastElementChild;
      if (!cell || cell.querySelector(".vip-num-rsv")) continue;
      var b = document.createElement("button");
      b.type = "button"; b.className = "vip-num-rsv"; b.textContent = "RESERVE";
      b.setAttribute("aria-label", "Reserve " + (row.getAttribute("data-num") || "this number"));
      b.style.cssText = RSV_BTN_CSS;
      cell.appendChild(b);
    }
  }
  if (RESERVE_ENABLED && typeof MutationObserver === "function") {
    var watchPanel = function (panel) {
      if (panel.__vipRsvWatch) return;
      panel.__vipRsvWatch = true;
      decorateSearchPanel(panel);
      new MutationObserver(function () { decorateSearchPanel(panel); }).observe(panel, { childList: true });
    };
    var findPanel = function () { var p = document.getElementById("vip-num-search-panel"); if (p) watchPanel(p); return p; };
    var startPanelWatch = function () {
      if (findPanel()) return;
      var bodyObs = new MutationObserver(function () { if (findPanel()) bodyObs.disconnect(); });
      bodyObs.observe(document.body, { childList: true });
    };
    if (document.body) startPanelWatch(); else document.addEventListener("DOMContentLoaded", startPanelWatch);
  }
  // Window capture phase, so the row link, the cart drawer and other page click handlers never see the click.
  window.addEventListener("click", function (e) {
    var b = e.target && e.target.closest && e.target.closest("button.vip-num-rsv");
    if (!b) return;
    e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    if (!RESERVE_ENABLED || b.__vipBusy) return;
    var row = b.closest("a.vip-num-hit");
    var m = row && (row.getAttribute("href") || "").match(/\/products\/([^/?#]+)/);
    if (!m) return;
    var num = row.getAttribute("data-num") || "";
    var wa = "https://wa.me/447555666555?text=" + encodeURIComponent("I'd like to reserve " + num.replace(/\s+/g, "") + " - Pre-Order");
    var label = b.textContent;
    b.__vipBusy = true; b.textContent = "…"; b.setAttribute("aria-busy", "true");
    var reset = function () { b.textContent = label; b.__vipBusy = false; b.removeAttribute("aria-busy"); };
    var fallback = function (text) {
      b.textContent = text || label;
      setTimeout(function () { reset(); window.open(wa, "_blank", "noopener"); }, 1200);
    };
    productInfo(m[1]).then(function (p) {
      var v = p && p.variants && p.variants[0];
      if (!p || !v || !v.available || !p.tags || p.tags.indexOf("preorder") === -1) return fallback("RESERVED");
      return reserveGo(v.id).then(function (res) {
        if (res.ok) return;
        if (res.reason === "basket") { reset(); showRowNotice(row, BASKET_MSG); return; }
        if (res.reason === "busy") { reset(); return; }
        fallback(res.reason === "sold" ? "RESERVED" : "");
      });
    }).catch(function () { fallback(); });
  }, true);

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", onLoad); else onLoad();
  window.addEventListener("pageshow", function (e) { if (e.persisted) onLoad(); });
})();
